import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';
import AuthShell from '../components/AuthShell';
import { authApi, formatApiErrorDetail } from '../lib/authApi';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const tokenFromUrl = params.get('token') || '';
  const [token, setToken] = useState(tokenFromUrl);
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pw !== confirm) { setError('Passwords do not match.'); return; }
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      await authApi.reset(token, pw);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthShell title="Password updated" subtitle="All set">
        <div className="rounded-md bg-[#E5F0EA] border border-[#4A7C5E]/40 p-4 text-[13px] text-[#2D5F4F]" data-testid="reset-success">
          <div className="flex items-start gap-2.5">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
            <p>Your password has been updated. Redirecting you to sign in…</p>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password" subtitle="Reset password">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        {!tokenFromUrl && (
          <Field label="Reset token" value={token} onChange={setToken} required placeholder="Paste the token from your reset link" testId="reset-token" />
        )}
        <Field label="New password" value={pw} onChange={setPw} type="password" required testId="reset-pw" autoComplete="new-password" />
        <Field label="Confirm new password" value={confirm} onChange={setConfirm} type="password" required testId="reset-confirm" autoComplete="new-password" />
        {error && (
          <div role="alert" className="flex items-start gap-2 text-[12.5px] text-[#7A2E0F] bg-[#FBE3D6] border border-[#E8B79A] rounded-md px-3 py-2.5">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <button type="submit" disabled={loading}
          className="w-full h-11 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[14px] font-medium hover:bg-[#2D5F4F] disabled:opacity-60"
          data-testid="reset-submit"
        >
          {loading ? 'Updating…' : 'Update password'}
        </button>
        <Link to="/login" className="inline-flex items-center gap-1.5 text-[13px] text-[#4A7C5E] hover:text-[#2D5F4F] font-medium pt-2"><ArrowLeft size={14} /> Back to sign in</Link>
      </form>
    </AuthShell>
  );
}

function Field({ label, value, onChange, type = 'text', required, testId, autoComplete, placeholder }) {
  return (
    <label className="block">
      <span className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-[#3A4A44]">{label}{required && <span className="text-[#A06628]"> *</span>}</span>
      <input
        type={type} required={required} autoComplete={autoComplete}
        value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full h-11 px-3.5 rounded-md bg-white border border-[#E5DCC4] text-[14px] text-[#1A2622] placeholder:text-[#A8B0AA] focus:border-[#4A7C5E] outline-none"
        data-testid={testId}
      />
    </label>
  );
}
